import { Spinner } from '@/components/loaders/Spinner';
import { cn } from '@/utils/cn';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
  'relative inline-flex items-center justify-center font-medium gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2  focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 hover:cursor-pointer transform-gpu',
  {
    variants: {
      variant: {
        primary:
          'bg-primary-800 text-typography border border-primary hover:bg-primary-900 hover:border-primary-900 active:bg-blue-800 active:border-blue-800 [&_svg]:text-typography',
        dark: 'bg-secondary text-white border border-secondary hover:bg-secondary-400 hover:border-secondary-400 active:bg-secondary-600 active:border-secondary-600 [&_svg]:text-white',
        destructive:
          'bg-error-600 text-white border border-error-600 hover:bg-error-400 hover:border-error-400 active:bg-error-400 active:border-error-800 [&_svg]:text-white',
        'outline-destructive':
          'bg-white text-error-500 border border-error-500 hover:bg-error-100 hover:text-error-400 active:bg-error-200 [&_svg]:text-error-500 hover:[&_svg]:text-error-400',
        secondary:
          'bg-background-50 text-typography-900 border border-border-100 hover:bg-background-50/50 hover:border-background-100 active:bg-background-200 active:border-background-200 [&_svg]:text-typography-900',
        default:
          'bg-white text-typography-900 border border-border hover:bg-background-700 hover:border-border-100 active:bg-background-600 active:border-border-100 [&_svg]:text-typography-900',
        dashed:
          'bg-white text-gray-900 border border-dashed border-gray-300 hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 active:border-gray-500 [&_svg]:text-gray-900',
        text: 'bg-transparent text-gray-900 border border-transparent hover:bg-gray-100 active:bg-gray-200 [&_svg]:text-gray-900',
        link: 'bg-transparent text-blue-600 border border-transparent hover:text-primary-900 active:text-blue-800 underline-offset-4 hover:underline [&_svg]:text-blue-600',
        outline:
          'bg-transparent text-primary border border-primary hover:bg-primary active:bg-secondary-100 [&_svg]:text-blue-600 hover:text-white',
        ghost:
          'bg-transparent border border-transparent hover:border-primary hover:border hover:text-primary hover:[&_svg]:text-primary focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:text-current',
        inline: 'bg-transparent border border-transparent focus-visible:ring-0 focus-visible:ring-offset-0',
        'input-icon':
          'absolute top-0 right-0 h-full px-3 py-2 bg-transparent text-gray-900 border border-transparent hover:bg-primary-900 active:bg-gray-200 [&_svg]:text-gray-900',
        icon: 'bg-transparent text-typography border border-transparent hover:bg-primary-900 active:bg-secondary-400 focus-visible:ring-0 [&_svg]:text-primary hover:text-white hover:[&_svg]:text-white',
        tab: 'relative flex h-full items-center gap-2 px-3 text-sm font-normal transition-colors rounded-none border-none text-muted-foreground hover:text-foreground data-[active=true]:text-foreground data-[active=true]:bg-white data-[active=true]:after:bg-primary data-[active=true]:after:absolute data-[active=true]:after:bottom-0 data-[active=true]:after:left-0 data-[active=true]:after:h-0.5 data-[active=true]:after:w-full focus-visible:ring-0 focus-visible:ring-offset-0'
      },
      size: {
        default: 'h-11 px-8 py-3',
        md: 'h-9 px-4 py-2 text-sm',
        sm: 'h-8 px-3.5 py-2 text-xs',
        xs: 'h-5 px-2 py-0.5 text-xxs',
        condensed: '',
        icon: 'h-11 w-11',
        fullWidth: 'h-11 w-full'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariantProps {
  asChild?: boolean;
  loading?: boolean;
  ref?: React.RefObject<HTMLButtonElement>;
}

const Button = ({ className, variant, size, asChild = false, ref, loading, children, ...props }: ButtonProps) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }), {
        'pointer-events-none relative disabled:opacity-100': loading,
        'disabled:opacity-50': loading && props.disabled
      })}
      ref={ref}
      disabled={loading ?? props.disabled}
      {...props}
    >
      {loading ? (
        <>
          <span className="invisible flex items-center gap-2">{children}</span>
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner variant="default" size="sm" />
          </span>
        </>
      ) : (
        children
      )}
    </Comp>
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
